import { createContext, useContext, useState, useCallback, ReactNode, useRef } from 'react';
import moment from 'jalali-moment';
import { ReserveHttp } from '@/services';
import { mapItems } from '@/services/mappers/program';
import parseError from '@/utils/parseError';

interface ProgramContextState {
  programs: SelfMealEntity[];
  groupedPrograms: Dictionary<SelfMealEntity[]>;
  isProgramsFetching: boolean;
  fetchError: any;
}

interface ProgramContextActions {
  fetchPrograms: (selfId: number, date?: string) => Promise<void>;
  registerFetchCallback: (callback: () => Promise<void>) => void;
  fetchCurrentSelfPrograms: () => Promise<void>;
}

interface ProgramContextType extends ProgramContextState, ProgramContextActions {}

const ProgramContext = createContext<ProgramContextType | undefined>(undefined);

interface ProgramProviderProps {
  children: ReactNode;
}

export const ProgramContextProvider = ({ children }: ProgramProviderProps) => {
  const [fetchError, setFetchError] = useState<any>(null);
  const [isProgramsFetching, setFetching] = useState<boolean>(true);
  const [programs, setPrograms] = useState<SelfMealEntity[]>([]);
  const [groupedPrograms, setGroupedPrograms] = useState<Dictionary<SelfMealEntity[]>>({});

  const fetchCallbackRef = useRef<(() => Promise<void>) | null>(null);

  const fetchPrograms = useCallback(async (selfId: number, date?: string) => {
    if (!selfId) return;
    const finalDate = date || moment().locale('en').format('YYYY-MM-DD');
    setFetching(true);
    try {
      setPrograms([]);
      setGroupedPrograms({});
      setFetchError(null);
      const response = await ReserveHttp.fetchSelfProgram({
        selectedSelfId: selfId,
        showingDate: `${finalDate}+00:00:00`
      });
      const { programs: mappedPrograms, groupedPrograms: mappedGroupedPrograms } = mapItems(
        response.data.payload,
        finalDate
      );
      setPrograms(mappedPrograms);
      setGroupedPrograms(mappedGroupedPrograms);
    }
    catch (e) {
      setPrograms([]);
      setGroupedPrograms({});
      setFetchError(parseError(e));
    }
    finally {
      setFetching(false);
    }
  }, []);

  const registerFetchCallback = useCallback((callback: () => Promise<void>) => {
    fetchCallbackRef.current = callback;
  }, []);

  const fetchCurrentSelfPrograms = useCallback(async () => {
    if (fetchCallbackRef.current) {
      await fetchCallbackRef.current();
    }
  }, []);

  const value: ProgramContextType = {
    programs,
    groupedPrograms,
    isProgramsFetching,
    fetchError,
    fetchPrograms,
    registerFetchCallback,
    fetchCurrentSelfPrograms,
  };

  return <ProgramContext.Provider value={value}>{children}</ProgramContext.Provider>;
};

export const useProgramContext = (): ProgramContextType => {
  const context = useContext(ProgramContext);
  if (context === undefined) {
    throw new Error('useProgramContext must be used within a ProgramProvider');
  }
  return context;
};
